import { getCookie } from "@litres/utils";
import type {
  OnetimeSID,
  UserSID,
  AuthIdentifierAvailabilitySerializer,
  PhoneUserInput,
  AllowedLoginPhoneCountriesEnum,
  OTPResponse,
  IApiResponseMeta,
  MFARequiredResponse,
  CaptchaJWTResponse,
  IApiResponse,
  ICountryWithOTPSupport,
} from "@litres-libs/types";
import { providerNameMap } from "./auth.types";
import type {
  SocialProviders,
  ProviderKeys,
  TLoginRequest,
  TRegisterRequest,
  RequestCaptchaCheck,
} from "./auth.types";

import { rtkqApi } from "../api/rtkqApi";

import {
  isAuthByLoginOrEmailUserInput,
  isAuthByOTPUserInput,
  isAuthByPhoneAndPasswordInput,
  isAuthByOneTimeCodeInput,
} from "./auth.utils";

export const authApi = rtkqApi.injectEndpoints({
  endpoints: build => ({
    getOneTimeSid: build.mutation<OnetimeSID["onetime_sid"], void>({
      query: () => {
        return {
          url: "/foundation/api/auth/onetime-sid",
          method: "POST",
        };
      },
      transformResponse: (response: IApiResponse<OnetimeSID>) =>
        response.payload.data.onetime_sid,
    }),
    getUserSid: build.mutation<UserSID["sid"], OnetimeSID["onetime_sid"]>({
      query(oneTimeSid) {
        return {
          url: "/foundation/api/auth/login/onetime-sid",
          method: "POST",
          body: {
            onetime_sid: oneTimeSid,
          },
        };
      },
      transformResponse: (response: IApiResponse<UserSID>) =>
        response.payload.data.sid,
    }),
    register: build.mutation<UserSID["sid"], TRegisterRequest>({
      query(body) {
        const safeMode = getCookie("safemode");
        const headers = {
          "Anonymous-Discount": undefined,
        };
        return {
          url: "/foundation/api/auth/register",
          method: "POST",
          body,
          headers: safeMode
            ? {
                ...headers,
                "Safemode-Enabled": safeMode,
              }
            : headers,
        };
      },
      transformResponse: (response: IApiResponse<UserSID>) =>
        response.payload.data.sid,
    }),
    login: build.mutation<UserSID["sid"], TLoginRequest>({
      query(payload) {
        const safeMode = getCookie("safemode");
        const headers = {
          "Anonymous-Discount": undefined,
        };

        let body: TLoginRequest;

        // NOTE: пришлось прибегнуть к type guards, чтобы не было ошибок при чтении payload
        if (isAuthByPhoneAndPasswordInput(payload)) {
          body = {
            phone: payload.phone,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            country_code: payload.country_code,
            password: payload.password,
          };
        } else if (isAuthByLoginOrEmailUserInput(payload)) {
          body = {
            login: payload.login,
            password: payload.password,
          };
        } else if (isAuthByOTPUserInput(payload)) {
          body = {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            otp_id: payload.otp_id,
            code: payload.code,
          };
        } else if (isAuthByOneTimeCodeInput(payload)) {
          body = {
            code: payload.code,
          };
        } else {
          /**
           * code: "2222", otp_id: "fe506127-27bb-4c3e-ab7e-f3ce709bddc8"
           */
          throw new TypeError(`Invalid "/foundation/api/auth/login" payload`);
        }

        return {
          url: "/foundation/api/auth/login",
          method: "POST",
          body,
          headers: safeMode
            ? {
                ...headers,
                "Safemode-Enabled": safeMode,
              }
            : headers,
        };
      },
      transformResponse: (response: IApiResponse<UserSID>) =>
        response.payload.data.sid,
    }),
    logout: build.mutation<UserSID["sid"], void>({
      query() {
        return {
          url: "/foundation/api/auth/logout",
          method: "POST",
        };
      },
    }),
    changePassword: build.mutation<
      UserSID["sid"],
      {
        password: string;
        password_repeated: string;
      }
    >({
      query({ password, password_repeated }) {
        return {
          url: `/foundation/api/auth/change-password`,
          method: "POST",
          body: {
            password,
            password_repeated,
          },
        };
      },
    }),
    /**
     * Вход/регистрация через социальную сеть
     */
    loginBySocialNetworkCallback: build.mutation<
      UserSID["sid"],
      {
        network: string;
        code: string;
        redirect_uri?: string;
        code_verifier?: string;
      }
    >({
      query({ network, code, redirect_uri, code_verifier }) {
        const body = code_verifier
          ? { code, redirect_uri, code_verifier }
          : { code, redirect_uri };

        return {
          url: `/foundation/api/auth/${network}/login`,
          method: "POST",
          body,
        };
      },
      transformResponse: (response: IApiResponse<UserSID>) =>
        response.payload.data.sid,
    }),
    /**
     * Отвязка социальной сети
     */
    disconnectSocialNetwork: build.mutation<string, string>({
      query(network) {
        return {
          url: `/foundation/api/auth/disconnect/${network}`,
          method: "POST",
        };
      },
    }),
    /**
     * Привязка социальной сети
     */
    connectSocialNetwork: build.mutation<
      UserSID["sid"],
      {
        network: string;
        code: string;
        redirect_uri?: string;
        code_verifier?: string;
      }
    >({
      query({ network, code, redirect_uri, code_verifier }) {
        const body = code_verifier
          ? { code, redirect_uri, code_verifier }
          : { code, redirect_uri };

        return {
          url: `/foundation/api/auth/connect/${network}`,
          method: "POST",
          body,
          headers: {
            ["client-host"]:
              window.location.host.includes("localhost") ||
              window.location.hostname.includes("stage")
                ? "www.litres.ru"
                : window.location.host,
          },
        };
      },
    }),
    /**
     * Получение списка социальных сетей, доступных для подключения
     * Дополнительно: получаем здесь так же признак того, что уже был получен бонус за подключение
     */
    getAvailableSocialNetworks: build.query<SocialProviders[], void>({
      query: () => ({
        url: "/foundation/api/auth/socials",
        method: "GET",
      }),
      transformResponse: (response: IApiResponse<SocialProviders[]>) => {
        return response.payload.data.map(socialProvider => ({
          ...socialProvider,
          provider:
            providerNameMap[socialProvider.provider as ProviderKeys] ||
            socialProvider.provider,
        }));
      },
    }),
    checkLoginAvailability: build.mutation<boolean, string>({
      query(loginOrEmail) {
        return {
          url: "/foundation/api/auth/login-available",
          method: "POST",
          body: {
            login: loginOrEmail,
          },
        };
      },
      transformResponse: (
        response: IApiResponse<AuthIdentifierAvailabilitySerializer>
      ) => response.payload.data.available,
    }),
    checkPhoneAvailability: build.mutation<boolean, PhoneUserInput>({
      query(payload) {
        return {
          url: "/foundation/api/auth/phone-available",
          method: "POST",
          body: {
            ...payload,
          },
        };
      },
      transformResponse: (
        response: IApiResponse<AuthIdentifierAvailabilitySerializer>
      ) => response.payload.data.available,
    }),
    sendOTPcode: build.mutation<
      { otp_id: string; retry_counter: string },
      {
        login?: string;
        country_code?: AllowedLoginPhoneCountriesEnum;
        /**
         * Ключ для демо OTP через Телеграм
         * @deprecated
         * TODO: выпилить после проведения демо
         */
        otpCascadeKey?: string;
      }
    >({
      query({ login, country_code, otpCascadeKey }) {
        const payload = country_code
          ? { phone: login, country_code }
          : { email: login };

        return {
          url: "/foundation/api/auth/send-otp",
          method: "POST",
          body: payload,
          headers: {
            ...(otpCascadeKey ? { "OTP-Cascade-Key": otpCascadeKey } : {}),
          },
        };
      },
      transformResponse: (
        response: IApiResponse<OTPResponse>,
        meta: IApiResponseMeta
      ) => {
        return {
          otp_id: response.payload.data.otp_id,
          retry_counter: meta?.headers["retry-after"],
        };
      },
    }),
    getMFAInfo: build.query<MFARequiredResponse, string>({
      query: requestID => ({
        url: `/foundation/api/auth/mfa/${requestID}`,
        method: "GET",
      }),
      transformResponse: (response: IApiResponse<MFARequiredResponse>) => {
        return response.payload.data;
      },
    }),
    otpCheck: build.mutation<
      void,
      {
        otp_id: string;
        code: string;
        otp_type: "email" | "sms";
      }
    >({
      query: body => ({
        url: "/foundation/api/auth/otp-check",
        method: "POST",
        body,
      }),
    }),
    captchaCheck: build.mutation<CaptchaJWTResponse, RequestCaptchaCheck>({
      query: body => ({
        url: "/foundation/api/auth/captcha-check",
        method: "POST",
        body,
      }),
      transformResponse: (response: IApiResponse<CaptchaJWTResponse>) => {
        return response.payload.data;
      },
    }),
    getAllowedPhoneCodes: build.query<ICountryWithOTPSupport[], void>({
      query: () => ({
        url: "/foundation/api/auth/countries/allowed",
        method: "GET",
      }),
      transformResponse: (
        response: IApiResponse<{ countries: ICountryWithOTPSupport[] }>
      ) => response.payload.data.countries,
    }),
  }),
});
